import axios from 'axios'
import parse from 'html-react-parser'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { BsArrowLeft } from 'react-icons/bs'
import {
  FacebookIcon,
  FacebookShareButton,
  LineIcon,
  LineShareButton,
  TelegramIcon,
  TelegramShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton
} from 'react-share'
import { useState } from 'react'
import ArticleLoader from 'src/components/atoms/ArticleLoader'
import CardArticle from 'src/components/molecules/CardArticle'
import getCustomMonth from 'src/data/month'
import useSwr from 'swr'

const fetcher = async url => {
  const response = await axios(url)
  return response.data
}

const ArticleDetail = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const slug = location.pathname.split('/').pop()
  const API = process.env.REACT_APP_API

  const { data, isLoading } = useSwr(
    `${API}/article/${slug}`,
    fetcher
  )

  const [keyword, setKeyword] = useState('')

  const article = data?.article
  const relatedByTag = data?.related?.by_article || []
  const relatedByCategory = data?.related?.by_category || []
  const tags = article?.tags || []

  const date = article
    ? new Date(article.published_at)
    : new Date()

  const timePub = `${date.getDate()} ${getCustomMonth(
    date.getMonth()
  )} ${date.getFullYear()}`

  const handleSearch = e => {
    if (e.key === 'Enter' && keyword.trim()) {
      navigate(`/articles/search/${keyword}`)
    }
  }

  if (isLoading || !article) return <ArticleLoader />

  return (
    <>
      <div className="py-8 md:py-10 px-4 sm:px-6 lg:px-14">
        {/* Breadcrumb + Back Button */}
        <div className="w-full bg-white/70 backdrop-blur border-b border-gray-200">
          <div className="max-w-7xl mx-auto min-h-[56px] pb-6 flex flex-wrap items-center gap-2 sm:gap-4">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-100 hover:bg-gray-200 text-xs sm:text-sm transition-colors"
              aria-label="Kembali"
            >
              <BsArrowLeft size={16} />

              <span>Kembali</span>

            </button>

            <span className="text-gray-300">/</span>

            <button
              onClick={() => navigate('/articles')}
              className="text-xs sm:text-sm text-gray-500 hover:text-[#2E7462] transition"
            >
              Articles
            </button>

            <span className="text-gray-300">/</span>

            <span
              className="text-xs sm:text-sm font-semibold text-[#0B132A] line-clamp-1"
            >
              {article.title}
            </span>
          </div>
        </div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8 md:space-y-10">
            {/* Article Header */}
            <div>
              <h1
                className=" text-[#2E7462] text-[26px] sm:text-[32px] md:text-[42px] font-semibold leading-snug break-words pt-2"
              >
                {article.title}
              </h1>

              <div
                className="mt-3 flex flex-wrap items-center gap-2 text-xs sm:text-sm text-slate-500"
              >
                <span>{timePub}</span>
                <span className="hidden sm:inline">|</span>

                <span
                  className="px-3 py-1 rounded-full bg-emerald-50 text-[#2E7462] font-medium"
                >
                  {article.category?.title}
                </span>
              </div>
            </div>

            {/* Featured Image */}
            <div className="rounded-2xl overflow-hidden bg-gray-100 shadow-sm">
              <img
                src={`${process.env.REACT_APP_IMAGE}/article/${article.image}`}
                alt={article.title}
                className=" w-full object-cover max-h-[240px] sm:max-h-[420px] md:max-h-[520px]"
              />

            </div>

            {/* Article Content */}
            <article
              className=" prose prose-sm sm:prose-base lg:prose-lg max-w-none prose-img:rounded-xl prose-headings:text-[#0B132A] prose-p:text-slate-700 prose-a:text-[#2E7462]"
            >
              {parse(article.body || '')}
            </article>
          </div>

          {/* Sidebar */}
          <aside className="space-y-8 lg:space-y-10">
            {/* Search */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <h3 className="text-[#2E7462] font-semibold mb-4">
                Cari Artikel
              </h3>

              <input
                type="text"
                placeholder="Ketik judul artikel..."
                value={keyword}
                onChange={e => setKeyword(e.target.value)}
                onKeyDown={handleSearch}
                className=" w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E7462] "
              />

            </div>


            {/* Tags */}
            {tags.length > 0 && (

              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <h3 className="text-[#2E7462] font-semibold mb-5">
                  Tags
                </h3>
                <div className="flex flex-wrap gap-3">
                  {tags.map(tag => (

                    <Link
                      key={tag}
                      to={`/articles/tag/${encodeURIComponent(tag)}`}
                      className=" px-4 py-2 rounded-full bg-emerald-50 hover:bg-emerald-100 border border-emerald-100 text-[#2E7462] text-sm font-medium transition "
                    >

                      #{tag}

                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Related Articles */}
            {relatedByCategory.length > 0 && (

              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <h3 className="text-[#2E7462] font-semibold mb-5">
                  Artikel Terkait
                </h3>

                <div className="space-y-5">

                  {relatedByCategory.map(item => (

                    <CardArticle
                      key={item.id}
                      slug={`/articles/${item.slug}`}
                      publishedAt={item.published_at}
                      image={`${process.env.REACT_APP_IMAGE}/article/${item.image}`}
                      title={item.title}
                    />

                  ))}

                </div>
              </div>
            )}

            {/* Share */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <h3 className="text-[#2E7462] font-semibold mb-5">
                Share Artikel
              </h3>

              <div className="flex flex-wrap gap-3">

                <WhatsappShareButton url={window.location.href}>
                  <WhatsappIcon size={40} round />
                </WhatsappShareButton>

                <TwitterShareButton url={window.location.href}>
                  <TwitterIcon size={40} round />
                </TwitterShareButton>

                <FacebookShareButton url={window.location.href}>
                  <FacebookIcon size={40} round />
                </FacebookShareButton>

                <TelegramShareButton url={window.location.href}>
                  <TelegramIcon size={40} round />
                </TelegramShareButton>

                <LineShareButton url={window.location.href}>
                  <LineIcon size={40} round />
                </LineShareButton>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Related Bottom */}
      {relatedByTag.length > 0 && (

        <div className="bg-gray-100 mt-10">
          <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-14">
            <div className="mb-8">
              <h2 className=" text-[#2E7462] text-xl md:text-2xl font-semibold ">
                Similiar News
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">

              {relatedByTag.map(item => (

                <CardArticle
                  key={item.id}
                  slug={`/articles/${item.slug}`}
                  publishedAt={item.published_at}
                  image={`${process.env.REACT_APP_IMAGE}/article/${item.image}`}
                  title={item.title}
                  type="horizontal"
                />

              ))}

            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ArticleDetail